/**
 * Lakehouse Data Sources – 4 loại: MongoDB, Postgres, MinIO, Firebase Storage
 * Big Data / Lakehouse: xem trạng thái kết nối và vào từng nguồn.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    LinearProgress,
    Tooltip
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { useGetLakehouseSourcesQuery } from 'store/slices/apiSlice';
import {
    IconDatabase,
    IconCloud,
    IconBucket,
    IconRefresh,
    IconArrowRight,
    IconCheck,
    IconX,
    IconAlertCircle
} from '@tabler/icons-react';

const SOURCE_CONFIG = {
    mongodb: {
        title: 'MongoDB',
        description: 'NoSQL document database',
        icon: IconDatabase,
        route: '/workspace/storage',
        routeLabel: 'Vào Workspace Storage (MongoDB theo project)',
        color: 'success'
    },
    postgres: {
        title: 'PostgreSQL',
        description: 'Relational database',
        icon: IconDatabase,
        route: '/database/dashboard',
        routeLabel: 'Database Dashboard',
        color: 'info'
    },
    minio: {
        title: 'MinIO',
        description: 'S3-compatible object storage (Lakehouse)',
        icon: IconBucket,
        route: '/storage/dashboard',
        routeLabel: 'Storage Dashboard (MinIO Lakehouse)',
        color: 'primary'
    },
    firebase_storage: {
        title: 'Firebase Storage',
        description: 'Google Cloud Storage / Firebase',
        icon: IconCloud,
        route: '/storage/dashboard',
        routeLabel: 'Storage (GCS/Firebase)',
        color: 'warning'
    }
};

const StatusChip = ({ status, message }) => {
    const theme = useTheme();
    if (status === 'healthy') {
        return (
            <Chip
                size="small"
                icon={<IconCheck size={14} />}
                label="Kết nối OK"
                color="success"
                sx={{ fontWeight: 600 }}
            />
        );
    }
    if (status === 'not_configured') {
        return (
            <Tooltip title={message || 'Chưa cấu hình'}>
                <Chip
                    size="small"
                    icon={<IconAlertCircle size={14} />}
                    label="Chưa cấu hình"
                    color="default"
                />
            </Tooltip>
        );
    }
    return (
        <Tooltip title={message || 'Lỗi kết nối'}>
            <Chip
                size="small"
                icon={<IconX size={14} />}
                label="Lỗi"
                color="error"
            />
        </Tooltip>
    );
};

const LakehouseDataSources = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { data, isLoading, isError, refetch } = useGetLakehouseSourcesQuery(undefined, { skip: false });

    const sources = data?.sources ?? [];
    const summary = data?.summary ?? { total: 0, healthy: 0 };

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={1}>
                    <IconBucket size={28} />
                    <Typography variant="h3">Lakehouse – Nguồn dữ liệu</Typography>
                </Box>
            }
            secondary={
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<IconRefresh size={18} />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                >
                    Làm mới
                </Button>
            }
        >
            {isLoading && <LinearProgress sx={{ mb: 2 }} />}
            {isError && (
                <Typography color="error" sx={{ mb: 2 }}>
                    Không tải được trạng thái. Kiểm tra đăng nhập và API.
                </Typography>
            )}
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Bốn nguồn dữ liệu cho Big Data / Lakehouse: MongoDB, PostgreSQL, MinIO, Firebase Storage. Bấm vào thẻ để mở trang quản lý tương ứng.
            </Typography>
            <Grid container spacing={gridSpacing}>
                {sources.map((source) => {
                    const config = SOURCE_CONFIG[source.source_type] || {};
                    const Icon = config.icon || IconDatabase;
                    return (
                        <Grid item xs={12} sm={6} md={3} key={source.source_type}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    cursor: config.route ? 'pointer' : 'default',
                                    borderColor:
                                        source.status === 'healthy'
                                            ? theme.palette.success.light
                                            : source.status === 'not_configured'
                                            ? theme.palette.grey[400]
                                            : theme.palette.error.light,
                                    '&:hover': config.route
                                        ? { boxShadow: 2, borderColor: theme.palette.primary.main }
                                        : {}
                                }}
                                onClick={() => config.route && navigate(config.route)}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Icon size={28} style={{ color: theme.palette[config.color || 'primary']?.main }} />
                                            <Typography variant="h5">{source.display_name || config.title}</Typography>
                                        </Box>
                                        <StatusChip status={source.status} message={source.message} />
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                        {config.description}
                                    </Typography>
                                    {source.details && Object.keys(source.details).length > 0 && (
                                        <Typography variant="caption" color="textSecondary" component="div">
                                            {source.details.endpoint && `Endpoint: ${source.details.endpoint}`}
                                            {source.details.database && ` • DB: ${source.details.database}`}
                                            {source.details.bucket && ` • Bucket: ${source.details.bucket}`}
                                            {source.details.project_id && ` • Project: ${source.details.project_id}`}
                                        </Typography>
                                    )}
                                    {config.route && (
                                        <Button
                                            size="small"
                                            endIcon={<IconArrowRight size={16} />}
                                            sx={{ mt: 1 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(config.route);
                                            }}
                                        >
                                            {config.routeLabel}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            {summary.total > 0 && (
                <Box sx={{ mt: 2 }} display="flex" gap={2} flexWrap="wrap">
                    <Chip label={`Tổng: ${summary.total} nguồn`} size="small" variant="outlined" />
                    <Chip label={`OK: ${summary.healthy}`} size="small" color="success" variant="outlined" />
                    {summary.unhealthy > 0 && (
                        <Chip label={`Lỗi: ${summary.unhealthy}`} size="small" color="error" variant="outlined" />
                    )}
                    {summary.not_configured > 0 && (
                        <Chip label={`Chưa cấu hình: ${summary.not_configured}`} size="small" variant="outlined" />
                    )}
                </Box>
            )}
        </MainCard>
    );
};

export default LakehouseDataSources;
